/**
 * =============================================================================
 * researchQuery — the filter parameters the registry list endpoint binds
 * =============================================================================
 *
 * Taken from the deployed Swagger on 2026-10-16
 * (`GET /api/Research/Research/{pageSize}/{pageNumber}`).
 *
 * THE NAMES MATTER LITERALLY. ASP.NET binds query parameters by name and
 * silently ignores one it does not recognise, so a wrong spelling is not an
 * error — it is a filter that does nothing, behind a 200 and a full-looking
 * table.
 *
 * ⚠ WHICH IS EXACTLY WHAT WAS HAPPENING. The CMS sent `search`, `SearchBy`,
 * `OrderBy` and `orderDir`. The endpoint takes `SearchText`, `SortBy` and
 * `SortDirection`. None of the four bound, so the search box returned the
 * unfiltered first page however it was used and the sort did nothing — which
 * is why the October review asked for "filters in addition to the existing
 * search functionality" without mentioning that search itself was inert.
 *
 * NOT AVAILABLE, though the review asked for it: a publication-year filter.
 * No such parameter exists on this endpoint. That is an ask for the backend
 * team; a control offered here would filter nothing while appearing to work.
 *
 * This lives apart from `Hooks/Applications.jsx` because it is a pure function
 * and that file imports `axios`, which Jest cannot transform — so a test of
 * the query string would drag an ESM dependency in behind it.
 */

export const RESEARCH_FILTER_PARAMS = Object.freeze([
  'SearchText',
  'SubmissionKind',
  'Status',
  'IsAcademyStudent',
  'IsFacultyMember',
  'IsDubaiPoliceEmployee',
  'SubmittedFrom',
  'SubmittedTo',
  'PublicationYearFrom',
  'PublicationYearTo',
  'SortBy',
  'SortDirection',
]);

/**
 * `Status` and the publication-year range were requested in Backend Request #2
 * (A1 and A5) and are wired here on the understanding that they ship with it.
 * Until they do the API ignores them, exactly as it ignores any unrecognised
 * parameter — the screens degrade to the filters that already work rather than
 * erroring.
 */

/** Every filter cleared. Shared so "reset" and "initial" cannot drift apart. */
export const EMPTY_RESEARCH_FILTERS = Object.freeze({
  SubmissionKind: '',
  Status: '',
  IsAcademyStudent: '',
  IsFacultyMember: '',
  IsDubaiPoliceEmployee: '',
  SubmittedFrom: '',
  SubmittedTo: '',
  PublicationYearFrom: '',
  PublicationYearTo: '',
});

/**
 * Submission statuses, per Backend Request #2 §A1.
 *
 * Three values, not four. The Academy's review listed "New / Read / Unread /
 * Archived", but "new" and "unread" describe the same state — a record nobody
 * has opened — and a workflow with two words for one state is one people get
 * wrong. Returning a record to the unread pile is `read -> new`.
 */
export const RESEARCH_STATUS = Object.freeze({
  NEW: 'new',
  READ: 'read',
  ARCHIVED: 'archived',
});

export const RESEARCH_STATUS_LABELS = Object.freeze({
  [RESEARCH_STATUS.NEW]: 'جديد',
  [RESEARCH_STATUS.READ]: 'مقروء',
  [RESEARCH_STATUS.ARCHIVED]: 'مؤرشف',
});

/**
 * The Arabic label for a status, or the dash for a record that predates the
 * field. Not "unknown", which would read as an error rather than as "this
 * record is older than the workflow".
 */
export const researchStatusLabel = (status) =>
  RESEARCH_STATUS_LABELS[String(status || '').toLowerCase()] || '-';

/**
 * =============================================================================
 * Publication year — what counts as one, and why it is checked HERE
 * =============================================================================
 *
 * THE BUG THIS FIXES. The year boxes committed on every keystroke, so typing
 * «2026» sent four requests — `PublicationYearFrom=2`, `=20`, `=202`, `=2026`
 * — and the API answered the first three with HTTP 400. The operator saw the
 * table flash an error three times before settling, and the network log filled
 * with failures that were never anybody's mistake.
 *
 * Debouncing the input is half the answer; it is in `ResearchFilters`. This is
 * the other half, and it belongs in the query builder rather than only in the
 * component: `buildResearchQuery` is the single place every research request
 * passes through — the table, the 10,000-row print set and the export all call
 * it — so a half-typed year cannot reach the API down ANY path, including one
 * written later by someone who has not read the component.
 *
 * A YEAR IS FOUR DIGITS, 1900 TO THIS YEAR. 1900 because the registry is a
 * record of modern scholarship and a 2- or 3-digit value is always a half-typed
 * one rather than a real year; this year as the ceiling because a publication
 * year in the future is not a thing that has happened yet.
 */
export const EARLIEST_PUBLICATION_YEAR = 1900;

export const latestPublicationYear = () => new Date().getFullYear();

/**
 * Is this a year the API can be asked about?
 *
 * Deliberately strict about the SHAPE as well as the range: `Number('2 ')` is
 * 2 and `Number('')` is 0, so a range check alone would pass values that are
 * not years at all.
 *
 * @param {string|number} value
 * @returns {boolean}
 */
export const isCompletePublicationYear = (value) => {
  const text = String(value ?? '').trim();
  if (!/^\d{4}$/.test(text)) return false;
  const year = Number(text);
  return year >= EARLIEST_PUBLICATION_YEAR && year <= latestPublicationYear();
};

/** The year parameters, so the builder and the component agree on which. */
export const PUBLICATION_YEAR_PARAMS = Object.freeze([
  'PublicationYearFrom',
  'PublicationYearTo',
]);

/**
 * Build the `Ids` repetition for a selected-records export (§A3).
 *
 * Repeated keys rather than a comma-joined list: that is what ASP.NET binds to
 * an array parameter without a custom model binder, and it survives ids that
 * might one day contain a comma.
 */
export const buildIdsQuery = (ids = []) => {
  const query = new URLSearchParams();
  ids.filter(Boolean).forEach((id) => query.append('Ids', id));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Build the query string, omitting anything the operator has not set.
 *
 * An unset filter must be ABSENT rather than sent empty: DPA controllers have
 * form answering 500 to a parameter with no value (see the `filterBy` note in
 * DPA-CONFORMANCE.md), and an empty `SubmittedFrom` is not a date.
 *
 * `URLSearchParams` does the encoding, which matters for Arabic search terms
 * and for anything containing `&`.
 *
 * @param {object} [filters]
 * @returns {string} `?a=b&c=d`, or '' when there is nothing to send
 */
export const buildResearchQuery = (filters = {}) => {
  const query = new URLSearchParams();

  RESEARCH_FILTER_PARAMS.forEach((key) => {
    const value = filters?.[key];
    if (value === undefined || value === null || value === '') return;

    // A HALF-TYPED YEAR IS NOT A FILTER, IT IS A 400. See the note above:
    // this is the last gate before the URL, so no caller — present or future
    // — can send `PublicationYearFrom=2` on its way to becoming 2026.
    if (PUBLICATION_YEAR_PARAMS.includes(key) && !isCompletePublicationYear(value)) {
      return;
    }

    query.append(key, value);
  });

  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export default buildResearchQuery;
